// Leaderboard.jsx — Phase 4C
// Agent leaderboard: composite score = (roleplayAvg/10)*0.5 + dialerBookingRate*0.5 × 100

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Trophy, Medal, Star, Phone, Brain, TrendingUp, Loader2, User } from 'lucide-react';
import { apiGet } from '../lib/api.js';
import TopBar from '../components/TopBar.jsx';
import { initials } from '../lib/format.js';

const PERIOD_OPTIONS = [
  { value: '7d',  label: '7 Days' },
  { value: '30d', label: '30 Days' },
  { value: 'all', label: 'All Time' },
];

function PodiumCard({ rank, entry, height }) {
  if (!entry) return <div style={{ height }} />;

  const colors = {
    1: { border: 'border-yellow-400/60',  bg: 'bg-yellow-400/10',  text: 'text-yellow-400',  icon: '🥇' },
    2: { border: 'border-slate-400/60',   bg: 'bg-slate-400/10',   text: 'text-slate-300',   icon: '🥈' },
    3: { border: 'border-amber-600/60',   bg: 'bg-amber-600/10',   text: 'text-amber-500',   icon: '🥉' },
  };
  const c = colors[rank] || colors[3];

  return (
    <div className={`panel border ${c.border} ${c.bg} p-5 flex flex-col items-center gap-3 rounded-2xl transition-all hover:scale-[1.02]`}
         style={{ minHeight: height }}>
      <div className="text-3xl">{c.icon}</div>
      <div className={`w-14 h-14 rounded-full flex items-center justify-center text-lg font-bold bg-civix-panelLight ${c.text} border-2 ${c.border} shrink-0`}>
        {entry.avatar
          ? <img src={entry.avatar} alt={entry.name} className="w-full h-full rounded-full object-cover" />
          : initials(entry.name)}
      </div>
      <div className="text-center">
        <div className="font-semibold text-white text-sm">{entry.name}</div>
        <div className={`text-2xl font-bold mt-1 ${c.text}`}>{entry.compositeScore}</div>
        <div className="text-xs text-white/40">composite score</div>
      </div>
      <div className="w-full space-y-1 pt-2 border-t border-civix-border/40">
        <div className="flex justify-between text-xs">
          <span className="text-white/50 flex items-center gap-1"><Brain className="w-3 h-3" /> Roleplay avg</span>
          <span className="text-white font-medium">{entry.roleplayAvg || '—'} <span className="text-white/30">({entry.roleplayCount})</span></span>
        </div>
        <div className="flex justify-between text-xs">
          <span className="text-white/50 flex items-center gap-1"><Phone className="w-3 h-3" /> Booking rate</span>
          <span className="text-white font-medium">{(entry.dialerBookingRate * 100).toFixed(0)}% <span className="text-white/30">({entry.dialerCalls} calls)</span></span>
        </div>
      </div>
    </div>
  );
}

export default function Leaderboard() {
  const [period, setPeriod] = useState('30d');

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['leaderboard', period],
    queryFn: () => apiGet('/leaderboard', { period }),
    staleTime: 60000,
  });

  const rows = data?.data || data || [];
  const top3 = rows.slice(0, 3);
  const rest = rows.slice(3);

  // Podium order: 2nd, 1st, 3rd for visual effect
  const podiumOrder = [top3[1], top3[0], top3[2]];
  const podiumRanks  = [2, 1, 3];
  const podiumHeights = ['180px', '220px', '160px'];

  return (
    <div className="space-y-6">
      <TopBar
        title="Agent Leaderboard"
        subtitle="Composite score: roleplay performance + dialer booking rate"
        actions={
          <div className="flex items-center gap-1 bg-civix-panelLight rounded-lg p-1">
            {PERIOD_OPTIONS.map((o) => (
              <button
                key={o.value}
                onClick={() => setPeriod(o.value)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${
                  period === o.value
                    ? 'bg-civix-purple text-white shadow-glow-purple'
                    : 'text-white/50 hover:text-white'
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
        }
      />

      {isLoading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-civix-purple animate-spin" /></div>
      ) : rows.length === 0 ? (
        <div className="panel p-12 text-center">
          <Trophy className="w-12 h-12 text-white/20 mx-auto mb-3" />
          <p className="text-white/50">No activity in this period yet.</p>
          <p className="text-white/30 text-sm mt-1">Agents appear here after completing roleplay sessions or AI dialer calls.</p>
        </div>
      ) : (
        <>
          {/* Podium */}
          {top3.length > 0 && (
            <div className="panel p-6">
              <div className="flex items-center gap-2 mb-6">
                <Trophy className="w-5 h-5 text-yellow-400" />
                <h2 className="font-semibold text-white">Top 3</h2>
                {isFetching && <Loader2 className="w-4 h-4 text-white/30 animate-spin ml-auto" />}
              </div>
              <div className="grid grid-cols-3 gap-4 items-end">
                {podiumOrder.map((entry, i) => (
                  <PodiumCard
                    key={podiumRanks[i]}
                    rank={podiumRanks[i]}
                    entry={entry}
                    height={podiumHeights[i]}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Full table */}
          {rows.length > 0 && (
            <div className="panel overflow-hidden">
              <div className="flex items-center gap-2 p-4 border-b border-civix-border">
                <TrendingUp className="w-4 h-4 text-civix-purple" />
                <h2 className="font-semibold text-white text-sm">Full Rankings</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-civix-border text-white/40 text-xs uppercase tracking-wider">
                      <th className="px-4 py-3 text-left w-12">Rank</th>
                      <th className="px-4 py-3 text-left">Agent</th>
                      <th className="px-4 py-3 text-right">Composite</th>
                      <th className="px-4 py-3 text-right">Roleplay Avg</th>
                      <th className="px-4 py-3 text-right">Booking Rate</th>
                      <th className="px-4 py-3 text-right">Calls</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, idx) => (
                      <tr
                        key={row.agentId}
                        className={`border-b border-civix-border/50 transition hover:bg-civix-panelLight/30 ${idx < 3 ? 'bg-civix-panelLight/10' : ''}`}
                      >
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${
                            row.rank === 1 ? 'bg-yellow-400/20 text-yellow-400' :
                            row.rank === 2 ? 'bg-slate-400/20 text-slate-300' :
                            row.rank === 3 ? 'bg-amber-600/20 text-amber-500' :
                            'bg-civix-panelLight text-white/50'
                          }`}>
                            {row.rank}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-civix-gradient flex items-center justify-center text-xs font-bold shrink-0">
                              {row.avatar
                                ? <img src={row.avatar} alt={row.name} className="w-full h-full rounded-full object-cover" />
                                : initials(row.name)}
                            </div>
                            <span className="font-medium text-white">{row.name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className={`font-bold text-base ${
                            row.rank === 1 ? 'text-yellow-400' :
                            row.rank === 2 ? 'text-slate-300' :
                            row.rank === 3 ? 'text-amber-500' :
                            'text-civix-teal'
                          }`}>{row.compositeScore}</span>
                        </td>
                        <td className="px-4 py-3 text-right text-white/70">
                          {row.roleplayAvg || '—'}
                          <span className="text-white/30 text-xs ml-1">({row.roleplayCount})</span>
                        </td>
                        <td className="px-4 py-3 text-right text-white/70">
                          {(row.dialerBookingRate * 100).toFixed(0)}%
                        </td>
                        <td className="px-4 py-3 text-right text-white/50 text-xs">
                          {row.dialerCalls} calls · {row.dialerBookings} booked
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
